import * as React from 'react';
import { InputNumber, InputNumberProps } from '@easyv/react-components';
import { isNil, isNumber, isSameNumber } from '@/utils';

export interface AnimationProInputNumberProps
  extends Omit<
    InputNumberProps,
    'onChange' | 'value' | 'onBlur' | 'onPointMoveStart' | 'onPointMoveEnd'
  > {
  precisionOnShow?: number;
  precisionOnSave?: number;
  name: string;
  value: number;
  isEuler?: boolean;
  min?: number;
  max?: number;
  onChange?: (name: string, value: number) => void;
  onBlur: (name: string, value: number) => void;
  inputToRealValue?: (value: number) => number;
  realToInputValue?: (value: number) => number;
}

export const AnimationProInputNumber: React.FC<AnimationProInputNumberProps> = (props) => {
  const {
    value,
    isEuler = false,
    name,
    min,
    max,
    precisionOnShow,
    precisionOnSave,
    onBlur,
    onChange,
    inputToRealValue,
    realToInputValue,
    ...rest
  } = props;

  const [showValue, setShowValue] = React.useState<number | undefined>(undefined);
  const [isInputting, setIsInputting] = React.useState(false);
  const oldValue = React.useRef<number>(); // 输入的同时引擎的真实值也发生变化了,所以只能在这里存储输入前的旧值,用于判断是否值发生变化

  React.useEffect(() => {
    // 如果正在输入框输入,则不受引擎数据变化影响
    if (isInputting) return;
    if (isNil(value)) {
      setShowValue(undefined);
      return;
    }
    let num: number | undefined = Number(value);
    if (isNaN(num)) num = 0;
    if (!isNil(num)) {
      num = realToInputValue ? realToInputValue(num) : num;
      if (precisionOnShow !== undefined && precisionOnShow >= 0) {
        const h = 10 ** precisionOnShow;
        num = Math.round(num * h) / h;
      }
    }
    setShowValue(num);
  }, [isInputting, precisionOnShow, realToInputValue, value]);

  const handleBlur = (num: number | undefined) => {
    // 输入没发生变化,不同步
    console.log('input', oldValue.current, num);

    if (isNumber(num)) {
      num = min === undefined ? num : Math.max(min, num);
      num = max === undefined ? num : Math.min(max, num);

      let showValue = num;
      if (precisionOnShow !== undefined && precisionOnShow >= 0) {
        const h = 10 ** precisionOnShow;
        showValue = Math.round(showValue * h) / h;
      }
      setShowValue(showValue);

      let saveNum = inputToRealValue ? inputToRealValue(num) : num;
      if (precisionOnSave !== undefined && precisionOnSave >= 0) {
        const h = 10 ** precisionOnSave;
        saveNum = Math.round(saveNum * h) / h;
      }

      if (!isNil(oldValue.current) && isSameNumber(oldValue.current, saveNum)) {
        return;
      }

      onBlur?.(name, saveNum);
    } else {
      setShowValue(value);
      onBlur?.(name, value);
    }
  };

  const handleChange = (num: number) => {
    if (isNumber(num)) {
      num = min === undefined ? num : Math.max(min, num);
      num = max === undefined ? num : Math.min(max, num);

      const saveNum = inputToRealValue ? inputToRealValue(num) : num;
      if (precisionOnSave !== undefined && precisionOnSave >= 0) {
        const h = 10 ** precisionOnSave;
        num = Math.round(saveNum * h) / h;
      }
    }
    if (num !== undefined) onChange?.(name, num);
  };

  const defaultStep =
    1 /
    Math.pow(
      10,
      precisionOnSave === 0 && precisionOnSave === 0 ? 1 : precisionOnSave || precisionOnShow || 0,
    );

  return (
    <InputNumber
      hideControl
      step={defaultStep}
      size='small'
      autoComplete='off'
      name={name}
      value={showValue}
      suffix={isEuler ? '°' : ''}
      min={min}
      max={max}
      onBlur={(e: any, value: number | undefined) => {
        handleBlur(value);
        oldValue.current = undefined;
        setIsInputting(false);
      }}
      onFocus={(e: any) => {
        let num = value;
        if (!isNil(num)) {
          num = realToInputValue ? realToInputValue(num) : num;
          if (precisionOnShow !== undefined && precisionOnShow >= 0) {
            const h = 10 ** precisionOnShow;
            num = Math.round(num * h) / h;
          }
        }
        setShowValue(num);
        oldValue.current = value;
        setIsInputting(true);
        // 如果setShowValue了新数字，则直接调用select()无效果
        requestAnimationFrame(() => {
          e.target.select();
        });
      }}
      onPointMoveStart={(value) => {
        setShowValue(value);
        value !== undefined && handleChange(value);
      }}
      onPointMoveEnd={(value) => {
        handleBlur(value);
        return value;
      }}
      onChange={(num: number) => {
        setShowValue(num);
        handleChange(num);
      }}
      {...rest}
    />
  );
};
